/* The Hair Doctor — product page sections (vanilla JS, no dependencies).
   Every component is initialised per section root, so several instances can live on one page
   and the Shopify theme editor can reload sections independently. */
(() => {
  'use strict';
  if (window.THD && window.THD.loaded) return;

  document.documentElement.classList.add('thd-js');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Money formatting (Shopify money_format placeholders) ---------- */
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const delimit = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision).split('.');
      return fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fixed[1] ? decimal + fixed[1] : '');
    };
    const m = fmt.match(re);
    if (!m) return fmt;
    const value = {
      amount: () => delimit(cents, 2),
      amount_no_decimals: () => delimit(cents, 0),
      amount_with_comma_separator: () => delimit(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => delimit(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => delimit(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => delimit(cents, 0, ' ', ''),
      amount_with_space_separator: () => delimit(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: () => delimit(cents, 2, ' ', '.'),
    }[m[1]];
    return fmt.replace(re, value ? value() : delimit(cents, 2));
  }

  /* ---------- Shared modal (image zoom + video) ---------- */
  let modal;
  let modalReturnFocus = null;
  function getModal() {
    if (modal) return modal;
    modal = document.createElement('dialog');
    modal.className = 'thd-modal thd-scope';
    modal.setAttribute('aria-label', 'Media viewer');
    modal.innerHTML = '<button class="thd-round-btn thd-modal__close" type="button" aria-label="Close"><svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button><div class="thd-modal__body"></div>';
    modal.style.padding = '0';
    document.body.append(modal);
    $('.thd-modal__close', modal).addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
    modal.addEventListener('close', () => {
      $('.thd-modal__body', modal).replaceChildren(); // removes the player so playback stops
      $$('.thd-modal__nav', modal).forEach((b) => b.remove());
      modal.classList.remove('is-video', 'is-landscape');
      if (modalReturnFocus) modalReturnFocus.focus();
    });
    return modal;
  }
  function openModal(content, { video = false, landscape = false } = {}) {
    const m = getModal();
    modalReturnFocus = document.activeElement;
    $('.thd-modal__body', m).replaceChildren(content);
    m.classList.toggle('is-video', video);
    m.classList.toggle('is-landscape', landscape);
    if (!m.open) m.showModal();
    $('.thd-modal__close', m).focus();
    return m;
  }
  const navButton = (dir, label, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `thd-round-btn thd-modal__nav thd-modal__nav--${dir}`;
    b.setAttribute('aria-label', label);
    b.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="${dir === 'prev' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    b.addEventListener('click', onClick);
    return b;
  };

  const pauseMedia = (scope) => {
    $$('video', scope).forEach((v) => v.pause());
    $$('iframe', scope).forEach((f) => {
      try {
        f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        f.contentWindow.postMessage('{"method":"pause"}', '*');
      } catch { /* cross-origin frames may refuse */ }
    });
  };

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    const gallery = $('[data-thd-gallery]', root);
    if (!gallery) return null;
    const slides = $$('[data-thd-slide]', gallery);
    const thumbs = $$('[data-thd-thumb]', gallery);
    const status = $('[data-thd-gallery-status]', gallery);
    const zoomBtn = $('[data-thd-zoom]', gallery);
    const stage = $('.thd-gallery__stage', gallery);
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    const track = $('.thd-gallery__slides', gallery);
    const counter = $('[data-thd-counter]', gallery);
    const sliderQuery = gallery.classList.contains('thd-gallery--mobile-slider') ? window.matchMedia('(max-width: 749px)') : null;
    const isSlider = () => !!(sliderQuery && sliderQuery.matches);

    const show = (i, { fromScroll = false } = {}) => {
      if (!slides.length) return;
      pauseMedia(slides[index]);
      index = (i + slides.length) % slides.length;
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      if (isSlider() && !fromScroll) track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
      thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === index);
        if (n === index) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      const thumb = thumbs[index];
      if (thumb && thumb.parentElement.scrollWidth > thumb.parentElement.clientWidth) {
        thumb.parentElement.scrollTo({ left: thumb.offsetLeft - thumb.parentElement.offsetLeft - 8, behavior: 'smooth' });
      }
      if (zoomBtn) zoomBtn.hidden = slides[index].dataset.type !== 'image';
      if (status) status.textContent = `${index + 1} / ${slides.length}`;
    };
    const showMedia = (mediaId) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) show(i);
    };

    thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));
    const prev = $('[data-thd-prev]', gallery);
    const next = $('[data-thd-next]', gallery);
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    if (slides.length < 2) { if (prev) prev.hidden = true; if (next) next.hidden = true; }

    // Mobile slider: the track scrolls natively; keep the index in sync with what is on screen
    if (sliderQuery) {
      let raf = 0;
      track.addEventListener('scroll', () => {
        if (!isSlider()) return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== index && slides[i]) show(i, { fromScroll: true });
        });
      }, { passive: true });
    }

    if (gallery.dataset.swipe !== 'false') {
      let startX = null;
      stage.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' && !isSlider()) startX = e.clientX; });
      stage.addEventListener('pointerup', (e) => {
        if (startX == null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => { startX = null; });
    }

    const zoom = () => {
      if (slides[index].dataset.type !== 'image') return;
      const img = document.createElement('img');
      const render = () => {
        const src = $('img', slides[index]);
        img.src = src.dataset.full || src.currentSrc || src.src;
        img.alt = src.alt;
      };
      render();
      const m = openModal(img);
      const images = slides.filter((s) => s.dataset.type === 'image');
      if (images.length > 1) {
        const step = (d) => {
          let i = index;
          do { i = (i + d + slides.length) % slides.length; } while (slides[i].dataset.type !== 'image');
          show(i); render();
        };
        m.append(navButton('prev', 'Previous image', () => step(-1)), navButton('next', 'Next image', () => step(1)));
      }
    };
    if (zoomBtn) zoomBtn.addEventListener('click', zoom);
    if (gallery.dataset.zoom !== 'false') {
      $$('[data-thd-slide][data-type="image"] img', gallery).forEach((img) => img.addEventListener('dblclick', zoom));
    }
    show(index);
    return { showMedia };
  }

  /* ---------- Bundle offers ("Buy more, save more") ---------- */
  function initBundle(root, money) {
    const el = $('[data-thd-bundle]', root);
    if (!el) return null;
    const tiers = $$('[data-thd-tier]', el);
    if (!tiers.length) return null;
    const useCompareAt = el.dataset.compareBasis === 'compare_at';
    let active = tiers.find((t) => t.classList.contains('is-selected')) || tiers[0];

    const fill = (template, vars) => template.replace('[savings]', vars.savings).replace('[percent]', vars.percent).replace('[qty]', vars.qty).replace('[discount_amount]', vars.discountAmount).replace('[discount]', vars.discount);
    // In the cards layout the shade selectors live below the row, linked by data-thd-tier-options
    const panelOf = (tier) => $(`[data-thd-tier-options="${tier.dataset.tierKey}"]`, el);
    const selectsOf = (tier) => $$('select[data-thd-tier-variant]', panelOf(tier) || tier);
    const recalc = (tier) => {
      const selects = selectsOf(tier);
      if (!selects.length) return; // single-variant product: the server-rendered prices are already right
      const opt = (s) => s.selectedOptions[0];
      const full = selects.reduce((n, s) => n + +opt(s).dataset.price, 0);
      const compare = selects.reduce((n, s) => n + (useCompareAt ? Math.max(+opt(s).dataset.compare, +opt(s).dataset.price) : +opt(s).dataset.price), 0);
      const price = Math.round((full * (100 - +tier.dataset.discount)) / 100);
      const savings = compare - price;
      const vars = { savings: money(savings), percent: compare > 0 ? Math.round((savings * 100) / compare) : 0, qty: tier.dataset.qty, discount: tier.dataset.discount, discountAmount: money(full - price).replace(/[.,]00(?=\D*$)/, '') };
      $('[data-thd-tier-price]', tier).textContent = money(price);
      const cmp = $('[data-thd-tier-compare]', tier);
      if (cmp) { cmp.hidden = savings <= 0; cmp.lastChild.textContent = money(compare); }
      const pill = $('[data-thd-tier-pill]', tier);
      if (pill) { pill.hidden = savings <= 0; pill.textContent = fill(pill.dataset.template, vars); }
      const sub = $('[data-thd-tier-sub]', tier);
      if (sub) sub.textContent = fill(sub.dataset.template, vars);
      const disc = $('[data-thd-tier-discount]', tier);
      if (disc) disc.textContent = fill(disc.dataset.template, vars);
    };
    const choose = (tier, focus = false) => {
      active = tier;
      tiers.forEach((t) => {
        const on = t === tier;
        t.classList.toggle('is-selected', on);
        $('.thd-tier__pick', t).setAttribute('aria-checked', String(on));
        $('.thd-tier__pick', t).tabIndex = on ? 0 : -1;
        const panel = panelOf(t);
        if (panel) panel.hidden = !on;
      });
      if (focus) $('.thd-tier__pick', tier).focus();
    };
    tiers.forEach((tier, i) => {
      const pick = $('.thd-tier__pick', tier);
      pick.addEventListener('click', () => choose(tier));
      pick.addEventListener('keydown', (e) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        choose(tiers[(i + step + tiers.length) % tiers.length], true);
      });
      selectsOf(tier).forEach((s) => s.addEventListener('change', () => recalc(tier)));
      recalc(tier);
    });
    choose(active);

    return {
      el,
      // What the selected offer puts in the cart; null when the page has no bundle picker
      selection(fallbackVariantId) {
        const selects = selectsOf(active);
        if (selects.some((s) => !s.value || s.selectedOptions[0].disabled)) return { error: 'Please choose an available shade for every item.' };
        const qty = +active.dataset.qty;
        const lines = selects.length
          ? selects.map((s) => ({ id: +s.value, title: s.selectedOptions[0].textContent.trim(), price: +s.selectedOptions[0].dataset.price }))
          : Array.from({ length: qty }, () => ({ id: +fallbackVariantId, title: el.dataset.productTitle, price: 0 }));
        return { key: `q${qty}`, label: active.dataset.label, discount: +active.dataset.discount, code: active.dataset.code, lines };
      },
      select(tierEl) { if (tiers.includes(tierEl)) choose(tierEl); },
    };
  }

  /* ---------- Rotating guarantee lines ---------- */
  function initRotators(root) {
    $$('[data-thd-rotator]', root).forEach((el) => {
      const msgs = $$('.thd-rotator__msg', el);
      if (msgs.length < 2) return;
      let i = 0;
      const ms = Math.max(1500, (+el.dataset.interval || 4) * 1000);
      setInterval(() => {
        if (document.hidden) return;
        msgs[i].classList.remove('is-active');
        msgs[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
        msgs[i].removeAttribute('aria-hidden');
      }, ms);
    });
  }

  /* ---------- Product form, variants, prices ---------- */
  function initProduct(root) {
    const gallery = initGallery(root);
    const form = $('form[data-thd-product-form]', root);
    const variantsEl = $('[data-thd-variants]', root);
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    const moneyFormat = root.dataset.moneyFormat;
    const moneyWithCurrency = root.dataset.moneyWithCurrencyFormat;
    const idInput = form ? form.elements.id : null;
    const qty = form ? $('input[name="quantity"]', form) : null;
    const message = form ? $('[data-thd-cart-message]', form) : null;

    const money = (cents, style) => {
      let out = formatMoney(cents, style === 'with_currency' ? moneyWithCurrency : moneyFormat);
      if (style === 'no_trailing_zeros') out = out.replace(/[.,]00(?=\D*$)/, '');
      return out;
    };
    const clampQty = (v) => {
      if (!qty) return 1;
      return Math.min(+qty.max || 999, Math.max(+qty.min || 1, Number.isFinite(v) ? Math.round(v) : 1));
    };

    const bundle = initBundle(root, (cents) => money(cents));
    root.thdBundle = bundle;
    const currency = root.dataset.currency || '';
    const track = (event, id, sel) => {
      const merchandise = sel.lines.reduce((n, l) => n + l.price, 0) / 100;
      window.dataLayer = window.dataLayer || [];
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({
        event, bundle_id: id, bundle_offer_name: sel.label, bundle_type: 'quantity',
        displayed_discount_percent: sel.discount, currency, merchandise_value: merchandise,
        value: (merchandise * (100 - sel.discount)) / 100,
        items: sel.lines.map((l) => ({ item_id: String(l.id), item_name: l.title, quantity: 1, price: l.price / 100 })),
      });
    };

    const renderPrice = (variant) => {
      $$('[data-thd-price]', root).forEach((block) => {
        const style = block.dataset.moneyStyle;
        const current = $('[data-thd-price-current]', block);
        const compare = $('[data-thd-price-compare]', block);
        const save = $('[data-thd-price-save]', block);
        const soldOut = $('[data-thd-price-soldout]', block);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (current) current.textContent = money(variant.price, style);
        if (compare) {
          compare.hidden = !onSale;
          if (onSale) $('[data-value]', compare).textContent = money(variant.compare_at_price, style);
        }
        if (save) {
          save.hidden = !onSale;
          if (onSale) {
            const amount = variant.compare_at_price - variant.price;
            const percent = Math.round((amount * 100) / variant.compare_at_price);
            save.textContent = save.dataset.template.replace('[amount]', money(amount, style)).replace('[percent]', percent);
          }
        }
        if (soldOut) soldOut.hidden = variant.available;
      });
    };
    const renderStock = (variant) => {
      $$('[data-thd-stock]', root).forEach((el) => {
        el.classList.toggle('is-out', !variant.available);
        $('[data-in]', el).hidden = !variant.available;
        $('[data-out]', el).hidden = variant.available;
      });
      $$('[data-thd-add], [data-thd-buy]', root).forEach((btn) => {
        btn.disabled = !variant.available;
        const label = $('[data-label]', btn);
        if (label && btn.dataset.soldOutLabel) label.textContent = variant.available ? btn.dataset.label : btn.dataset.soldOutLabel;
      });
    };

    const picker = $('[data-thd-variant-picker]', root);
    if (picker && variants.length) {
      const onChange = () => {
        const selected = $$('[data-option-index]', picker).map((field) => {
          if (field.tagName === 'SELECT') return field.value;
          const checked = $('input:checked', field);
          return checked ? checked.value : null;
        });
        const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (!variant) {
          $$('[data-thd-add], [data-thd-buy]', root).forEach((b) => { b.disabled = true; });
          return;
        }
        if (idInput) idInput.value = variant.id;
        renderPrice(variant);
        renderStock(variant);
        if (variant.featured_media && gallery) gallery.showMedia(variant.featured_media.id);
        if (!window.Shopify || !window.Shopify.designMode) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      };
      picker.addEventListener('change', onChange);
    }

    if (form && qty) {
      $$('[data-thd-qty-step]', form).forEach((btn) => btn.addEventListener('click', () => {
        qty.value = clampQty(+qty.value + +btn.dataset.thdQtyStep);
      }));
      qty.addEventListener('change', () => { qty.value = clampQty(+qty.value); });
    }

    async function addToCart({ thenCheckout = false } = {}) {
      const afterAdd = thenCheckout ? 'checkout' : form.dataset.afterAdd;
      const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const useThemeCart = afterAdd === 'drawer' && cartUi && typeof cartUi.renderContents === 'function';
      const sections = useThemeCart && typeof cartUi.getSectionsToRender === 'function' ? cartUi.getSectionsToRender().map((s) => s.id) : null;
      if (sections && typeof cartUi.setActiveElement === 'function') cartUi.setActiveElement(document.activeElement);
      const sel = bundle ? bundle.selection(form.elements.id.value) : null;
      if (sel && sel.error) throw new Error(sel.error);

      let body;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      let bundleId = null;
      if (sel) {
        // Same line-item properties as the previous THD bundle block, so orders and reports stay consistent
        bundleId = `${sel.key}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const items = sel.lines.map((l, i) => ({
          id: l.id, quantity: 1,
          properties: { '_Bundle ID': bundleId, '_Bundle offer': sel.label, '_Bundle type': 'quantity', '_Bundle item': `${i + 1} of ${sel.lines.length}` },
        }));
        body = JSON.stringify(sections ? { items, sections, sections_url: location.pathname } : { items });
        headers['Content-Type'] = 'application/json';
      } else {
        body = new FormData(form);
        if (qty) body.set('quantity', clampQty(+qty.value));
        if (sections) { body.append('sections', sections); body.append('sections_url', location.pathname); }
      }
      const res = await fetch(`${rootUrl}cart/add.js`, { method: 'POST', headers, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      // Dawn's cart notification looks the added line up by its key
      if (!data.key && data.items && data.items[0]) data.key = data.items[0].key;
      if (sel) {
        track(afterAdd === 'checkout' ? 'bundle_buy_now' : 'bundle_add_to_cart', bundleId, sel);
        if (sel.code) await fetch(`${rootUrl}discount/${encodeURIComponent(sel.code)}`, { credentials: 'same-origin' }).catch(() => {});
      }

      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: form.elements.id.value, cartData: data });
      }
      document.dispatchEvent(new CustomEvent('thd:cart:added', { detail: data }));

      if (afterAdd === 'checkout') { location.href = `${rootUrl}checkout${sel && sel.code ? `?discount=${encodeURIComponent(sel.code)}` : ''}`; return; }
      if (afterAdd === 'cart') { location.href = `${rootUrl}cart`; return; }
      if (useThemeCart) {
        cartUi.renderContents(data);
        cartUi.classList.remove('is-empty');
        return;
      }
      if (message) message.innerHTML = `${form.dataset.addedText || 'Added to your cart.'} <a href="${rootUrl}cart">${form.dataset.viewCartText || 'View cart'}</a>`;
    }

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = $('[data-thd-add]', form);
        if (btn) btn.setAttribute('aria-busy', 'true');
        if (message) { message.textContent = ''; message.classList.remove('is-error'); }
        try {
          await addToCart();
        } catch (err) {
          if (message) { message.textContent = err.message; message.classList.add('is-error'); }
        } finally {
          if (btn) btn.removeAttribute('aria-busy');
        }
      });
      const buy = $('[data-thd-buy]', form);
      if (buy) buy.addEventListener('click', async () => {
        buy.setAttribute('aria-busy', 'true');
        try {
          await addToCart({ thenCheckout: true });
        } catch (err) {
          if (message) { message.textContent = err.message; message.classList.add('is-error'); }
          buy.removeAttribute('aria-busy');
        }
      });
    }
  }

  /* ---------- Coupon copy ---------- */
  function initCoupons(root) {
    $$('[data-thd-copy]', root).forEach((btn) => btn.addEventListener('click', async () => {
      const code = btn.dataset.thdCopy;
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        const ta = Object.assign(document.createElement('textarea'), { value: code });
        document.body.append(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      const original = btn.textContent;
      btn.textContent = btn.dataset.copiedText || 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 1800);
    }));
  }

  /* ---------- Video carousel ---------- */
  function initCarousel(root) {
    const carousel = $('[data-thd-carousel]', root);
    if (!carousel) return;
    const track = $('[data-thd-track]', carousel);
    const cards = $$('.thd-vcard', track);
    const prev = $('[data-thd-prev]', carousel);
    const next = $('[data-thd-next]', carousel);
    const dotsWrap = $('[data-thd-dots]', root);
    if (!cards.length) return;
    let dots = [];

    const step = () => cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
    const perPage = () => Math.max(1, Math.floor((track.clientWidth + 1) / step()));
    const pageCount = () => Math.max(1, Math.ceil((cards.length - perPage()) / perPage()) + 1);
    const maxScroll = () => track.scrollWidth - track.clientWidth;
    const goToPage = (p) => track.scrollTo({ left: p >= pageCount() - 1 ? maxScroll() : p * perPage() * step(), behavior: 'smooth' });

    const buildDots = () => {
      if (!dotsWrap) return;
      const n = pageCount();
      if (dots.length === n) return;
      dotsWrap.replaceChildren();
      dots = Array.from({ length: n }, (_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Show page ${i + 1} of ${n}`);
        b.addEventListener('click', () => goToPage(i));
        dotsWrap.append(b);
        return b;
      });
      dotsWrap.hidden = n < 2;
    };
    const update = () => {
      const x = track.scrollLeft;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= maxScroll() - 2;
      const page = x >= maxScroll() - 2 ? pageCount() - 1 : Math.round(x / (perPage() * step()));
      dots.forEach((d, i) => { if (i === page) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
    };
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -perPage() * step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: perPage() * step(), behavior: 'smooth' }));
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    new ResizeObserver(() => { buildDots(); update(); }).observe(track);

    // Mouse drag (touch devices scroll natively)
    let drag = null;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: track.scrollLeft, moved: false, id: e.pointerId };
    });
    track.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 5) { drag.moved = true; track.classList.add('is-dragging'); track.setPointerCapture(drag.id); }
      if (drag.moved) track.scrollLeft = drag.left - dx;
    });
    const endDrag = () => {
      if (!drag) return;
      const moved = drag.moved;
      drag = null;
      if (!moved) return;
      track.classList.remove('is-dragging');
      track.scrollTo({ left: Math.round(track.scrollLeft / step()) * step(), behavior: 'smooth' });
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', (e) => e.preventDefault());

    // Autoplay rotation (off by default; pauses on hover/focus)
    const autoplay = +carousel.dataset.autoplay;
    if (autoplay && !(window.Shopify && window.Shopify.designMode)) {
      let paused = false;
      ['mouseenter', 'focusin', 'pointerdown'].forEach((ev) => carousel.addEventListener(ev, () => { paused = true; }));
      ['mouseleave', 'focusout'].forEach((ev) => carousel.addEventListener(ev, () => { paused = false; }));
      setInterval(() => {
        if (paused || document.hidden) return;
        if (track.scrollLeft >= maxScroll() - 2) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      }, autoplay * 1000);
    }

    // Play videos
    const landscape = carousel.dataset.orientation === 'landscape';
    $$('[data-thd-video]', track).forEach((btn) => btn.addEventListener('click', () => {
      const { thdVideo: type, videoId, videoUrl } = btn.dataset;
      const title = btn.getAttribute('aria-label') || '';
      const frag = document.createDocumentFragment();
      if (type === 'mp4') {
        const video = Object.assign(document.createElement('video'), { src: videoUrl, controls: true, autoplay: true, playsInline: true });
        frag.append(video);
      } else if (type === 'shopify') {
        const tpl = $('template', btn.closest('.thd-vcard'));
        if (!tpl) return;
        const node = tpl.content.cloneNode(true);
        const video = $('video', node);
        if (video) { video.autoplay = true; video.controls = true; video.playsInline = true; }
        frag.append(node);
      } else {
        const iframe = document.createElement('iframe');
        iframe.src = type === 'vimeo'
          ? `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1`
          : `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&playsinline=1`;
        iframe.title = title;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        frag.append(iframe);
        if (videoUrl && type !== 'mp4') {
          frag.append(Object.assign(document.createElement('a'), {
            className: 'thd-modal__link', href: videoUrl, target: '_blank', rel: 'noopener',
            textContent: carousel.dataset.fallbackText || 'Watch on YouTube',
          }));
        }
      }
      openModal(frag, { video: true, landscape });
    }));

    return { showCard: (el) => track.scrollTo({ left: el.offsetLeft - track.offsetLeft, behavior: 'smooth' }) };
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq(root) {
    const accordion = $('[data-thd-accordion]', root);
    if (!accordion) return;
    const single = accordion.dataset.single === 'true';
    const buttons = $$('.thd-faq__item > h3 > button', accordion);
    const setOpen = (btn, open) => {
      btn.closest('.thd-faq__item').classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', String(open));
    };
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        if (open && single) buttons.forEach((b) => b !== btn && setOpen(b, false));
        setOpen(btn, open);
      });
      btn.addEventListener('keydown', (e) => {
        const i = buttons.indexOf(btn);
        const target = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: buttons.length - 1 }[e.key];
        if (target === undefined) return;
        e.preventDefault();
        buttons[(target + buttons.length) % buttons.length].focus();
      });
    });
    return { open: (btn) => setOpen(btn, true) };
  }

  /* ---------- Related products (Shopify recommendations API) ---------- */
  function initRelated(root) {
    const el = $('[data-thd-recommendations]', root);
    if (!el || !el.dataset.url) return;
    fetch(el.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-thd-recommendations]');
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
        else if (!(window.Shopify && window.Shopify.designMode)) root.closest('.shopify-section').hidden = true;
      })
      .catch(() => {});
  }

  /* ---------- Bootstrapping ---------- */
  const instances = new WeakMap();
  function init(scope = document) {
    $$('[data-thd-section]', scope).forEach((root) => {
      if (instances.has(root)) return;
      const api = {};
      switch (root.dataset.thdSection) {
        case 'product': initProduct(root); initCoupons(root); initRotators(root); break;
        case 'videos': api.carousel = initCarousel(root); break;
        case 'faq': api.faq = initFaq(root); break;
        case 'related': initRelated(root); break;
        default: break;
      }
      instances.set(root, api);
    });
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-thd-section]');
    if (!root) return;
    const api = instances.get(root) || {};
    if (api.faq) { const btn = $('h3 > button', e.target); if (btn) api.faq.open(btn); }
    if (api.carousel && e.target.classList.contains('thd-vcard')) api.carousel.showCard(e.target);
    if (root.thdBundle && e.target.matches('[data-thd-tier]')) root.thdBundle.select(e.target);
  });

  window.THD = { loaded: true, init, formatMoney };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
